import type { AvailabilityStatus, LineupEntryRecord, PlayerRatingBasis } from '../types';

export type WorkspaceMoveTarget = { positionId: string; positionCode: string; slotNumber: number };

/** Render the destination immediately; canonical IDs are reconciled after the write. */
export function optimisticWorkspaceMove(
  entries: LineupEntryRecord[],
  matchId: string,
  playerId: string,
  target?: WorkspaceMoveTarget,
): LineupEntryRecord[] {
  const source = entries.find((entry) => entry.player_id === playerId);
  const occupant = target && entries.find((entry) =>
    entry.league_configuration_position_id === target.positionId && entry.slot_number === target.slotNumber);
  if (!target) return source ? entries.filter((entry) => entry.id !== source.id) : entries;
  if (source && occupant && source.id === occupant.id) return entries;
  if (source) return entries
    .filter((entry) => entry.id !== occupant?.id)
    .map((entry) => entry.id === source.id
      ? { ...entry, league_configuration_position_id: target.positionId, position_code: target.positionCode, slot_number: target.slotNumber }
      : entry);
  if (occupant) return entries.map((entry) => entry.id === occupant.id ? { ...entry, player_id: playerId } : entry);
  return [...entries, {
    id: `optimistic:${playerId}:${target.positionId}:${target.slotNumber}`,
    match_event_id: matchId,
    league_configuration_position_id: target.positionId,
    position_code: target.positionCode,
    slot_number: target.slotNumber,
    player_id: playerId,
  }];
}

export type LineupWorkspacePlayer = {
  id: string;
  first_name: string;
  last_name: string;
  sex?: 'male' | 'female' | null;
  player_tier: string | number | null;
  estimated_dynamic_rating: number | null;
  availability_status: AvailabilityStatus;
};

export type WorkspacePlayerGenderGroup<T extends LineupWorkspacePlayer> = {
  key: 'all' | 'female' | 'male' | 'unspecified';
  label: string | null;
  players: T[];
};

export const lineupAvailabilityGroups: Array<{ status: AvailabilityStatus; label: string }> = [
  { status: 'available', label: 'Available' },
  { status: 'maybe', label: 'Maybe' },
  { status: 'not_available', label: 'Unavailable' },
  { status: 'unknown', label: 'Unknown / No Response' },
];

/** Availability is the non-negotiable first sort for the workspace pool. */
export function workspaceAvailabilityRank(status: AvailabilityStatus): number {
  if (status === 'available') return 0;
  if (status === 'maybe') return 1;
  if (status === 'unknown') return 2;
  return 3;
}

/**
 * Keeps the workspace pool as one continuous grid while preserving availability
 * as the hard, user-visible ordering rule. `recommendationPriority` is an
 * explainable composite supplied by the recommendation data already loaded by
 * the builder; it is intentionally not rendered as a score.
 */
export function sortWorkspacePoolPlayers<T extends LineupWorkspacePlayer>(
  players: T[],
  recommendationPriority: (player: T) => number = () => 0,
): T[] {
  return [...players].sort((a, b) => {
    const availability = workspaceAvailabilityRank(a.availability_status) - workspaceAvailabilityRank(b.availability_status);
    if (availability) return availability;
    const recommendation = recommendationPriority(b) - recommendationPriority(a);
    if (recommendation) return recommendation;
    const firstName = a.first_name.localeCompare(b.first_name);
    if (firstName) return firstName;
    const lastName = a.last_name.localeCompare(b.last_name);
    return lastName || a.id.localeCompare(b.id);
  });
}

/** Preserve the pool's canonical sort while adding optional visual gender sections. */
export function groupWorkspacePoolPlayersByGender<T extends LineupWorkspacePlayer>(
  players: T[],
  enabled: boolean,
): WorkspacePlayerGenderGroup<T>[] {
  if (!enabled) return [{ key: 'all', label: null, players }];
  return [
    { key: 'female' as const, label: 'Women', players: players.filter((player) => player.sex === 'female') },
    { key: 'male' as const, label: 'Men', players: players.filter((player) => player.sex === 'male') },
    { key: 'unspecified' as const, label: 'Unspecified', players: players.filter((player) => player.sex !== 'female' && player.sex !== 'male') },
  ].filter((group) => group.players.length > 0);
}

export function workspaceTierRank(value: string | number | null | undefined): number {
  const matched = String(value ?? '').trim().toLowerCase().match(/^(?:tier[_\s-]*)?([1-4])$/);
  return matched ? Number(matched[1]) : 5;
}

export function compareWorkspacePlayers<T extends LineupWorkspacePlayer>(a: T, b: T, ratingBasis: PlayerRatingBasis): number {
  const ranking = ratingBasis === 'tier'
    ? workspaceTierRank(a.player_tier) - workspaceTierRank(b.player_tier)
    : numeric(b.estimated_dynamic_rating) - numeric(a.estimated_dynamic_rating);
  if (ranking) return ranking;
  const firstName = a.first_name.localeCompare(b.first_name);
  if (firstName) return firstName;
  const lastName = a.last_name.localeCompare(b.last_name);
  return lastName || a.id.localeCompare(b.id);
}

export function groupWorkspacePlayers<T extends LineupWorkspacePlayer>(players: T[], ratingBasis: PlayerRatingBasis) {
  return lineupAvailabilityGroups.map((group) => ({
    ...group,
    players: players
      .filter((player) => player.availability_status === group.status)
      .sort((a, b) => compareWorkspacePlayers(a, b, ratingBasis)),
  }));
}

/**
 * Canonical Player × Team × Season metrics for the lineup workspace.
 * `nonCompletedAssignments` excludes the match currently being edited; the
 * current assignment is deliberately added only when the player is actually
 * in a slot, which prevents a pool-to-slot move from being counted twice.
 */
export function lineupTileMetrics(input: {
  completedPlayed: number;
  nonCompletedAssignments: number;
  currentMatchAssigned: boolean;
  target: number | null;
}) {
  const unplayed = input.nonCompletedAssignments + (input.currentMatchAssigned ? 1 : 0);
  const targetDelta = input.target === null ? null : input.completedPlayed + unplayed - input.target;
  return { played: input.completedPlayed, unplayed, targetDelta };
}

function numeric(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
}
